import {
  createAchSetupIntent,
  fetchBillingDetail,
  fetchStripeSources,
  finalizeAchSetupIntent,
  selectBillingDetail,
} from "@app/billing";
import { resetLoaderByIds } from "@app/loaders";
import { selectOrganizationSelected } from "@app/organizations";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useQuery,
  useSelector,
} from "@app/react";
import { billingPaymentMethodsUrl } from "@app/routes";
import { selectCurrentUser } from "@app/users";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Banner,
  BannerMessages,
  Box,
  Button,
  Group,
  Loading,
  TitleBar,
} from "../shared";
import { StripeProvider } from "./billing-method";

const ACH_UNAVAILABLE =
  "ACH enrollment is unavailable for this account. Contact support for help.";
const FINALIZEABLE_SETUP_STATUSES = ["succeeded", "requires_action"];
const SETUP_STILL_PROCESSING =
  "Bank verification is still processing. Wait a moment and save again.";

function AchSetupForm({
  billingDetailId,
  email,
  confirmedSetupIntentId,
  onConfirmedSetupIntent,
}: {
  billingDetailId: string;
  email: string;
  confirmedSetupIntentId: string;
  onConfirmedSetupIntent: (id: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const finalizeLoader = useLoader(finalizeAchSetupIntent);
  const isSaving = submitting || finalizeLoader.isLoading;

  useLoaderSuccess(finalizeLoader, () => {
    dispatch(fetchStripeSources({ id: billingDetailId }));
    navigate(billingPaymentMethodsUrl());
  });

  useEffect(() => {
    if (finalizeLoader.isError) {
      setSubmitting(false);
    }
  }, [finalizeLoader.isError]);

  async function submit() {
    if (!stripe || !elements) {
      throw new Error("stripe not found");
    }

    let setupIntentId = confirmedSetupIntentId;
    if (setupIntentId === "") {
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          // Required by Stripe. If a bank uses a full-page redirect, the
          // payment-methods page finalizes from the setup_intent query param.
          return_url: `${window.location.origin}${billingPaymentMethodsUrl()}`,
        },
        redirect: "if_required",
      });

      if (confirmError) {
        throw new Error(confirmError.message ?? "Unknown error");
      }

      if (!setupIntent) {
        throw new Error("SetupIntent not found");
      }

      setupIntentId = setupIntent.id;
      onConfirmedSetupIntent(setupIntent.id);
      if (!FINALIZEABLE_SETUP_STATUSES.includes(setupIntent.status)) {
        throw new Error(SETUP_STILL_PROCESSING);
      }
    }

    dispatch(
      finalizeAchSetupIntent({
        id: billingDetailId,
        setupIntentId,
      }),
    );
  }

  const onSubmitForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSaving) {
      return;
    }
    setError("");
    setSubmitting(true);
    submit().catch((err) => {
      setError(err.message);
      setSubmitting(false);
    });
  };

  return (
    <form onSubmit={onSubmitForm} className="space-y-4">
      {error && <Banner variant="error">{error}</Banner>}
      <BannerMessages {...finalizeLoader} />
      <PaymentElement
        options={{
          defaultValues: {
            billingDetails: {
              email,
            },
          },
        }}
      />
      <div className="flex gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate(billingPaymentMethodsUrl())}
        >
          Cancel
        </Button>
        <Button type="submit" isLoading={isSaving}>
          Save Bank Account
        </Button>
      </div>
    </form>
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <Group>
      <TitleBar description="Add a bank account for ACH payments">
        Add Bank Account
      </TitleBar>
      {children}
    </Group>
  );
}

export const BillingAddBankAccountPage = () => {
  const org = useSelector(selectOrganizationSelected);
  const billingDetailId = org.billingDetailId || org.id;
  const billingDetail = useSelector(selectBillingDetail);
  const user = useSelector(selectCurrentUser);
  const dispatch = useDispatch();
  // Keyed on the billing detail so a double invoke of the effect below, which
  // React does in StrictMode, creates at most one SetupIntent.
  const createdForRef = useRef("");
  const [clientSecret, setClientSecret] = useState("");
  const [confirmedSetupIntentId, setConfirmedSetupIntentId] = useState("");

  useQuery(fetchBillingDetail({ id: billingDetailId }));
  const pageLoader = useLoader(fetchBillingDetail({ id: billingDetailId }));
  const createLoader = useLoader(createAchSetupIntent);

  useLoaderSuccess(createLoader, () => {
    const secret = createLoader.meta?.clientSecret;
    if (typeof secret === "string") {
      setClientSecret(secret);
    }
  });

  // The reset has to happen in the same call as the create dispatch: as a
  // separate effect it can re-run afterwards and clear the loader for the
  // create that just started. finalize is reset because this page renders its
  // banner.
  const startSetupIntent = useCallback(() => {
    createdForRef.current = billingDetailId;
    setClientSecret("");
    setConfirmedSetupIntentId("");
    dispatch(
      resetLoaderByIds({
        ids: [`${createAchSetupIntent}`, `${finalizeAchSetupIntent}`],
      }),
    );
    dispatch(createAchSetupIntent({ id: billingDetailId }));
  }, [billingDetailId, dispatch]);

  const isDetailLoaded = billingDetail.id === billingDetailId;
  const isEligible = isDetailLoaded && billingDetail.invoicedExternally;

  useEffect(() => {
    if (!isEligible || createdForRef.current === billingDetailId) {
      return;
    }
    startSetupIntent();
  }, [isEligible, billingDetailId, startSetupIntent]);

  if (pageLoader.isLoading || (!pageLoader.isSuccess && !pageLoader.isError)) {
    return (
      <PageShell>
        <Loading />
      </PageShell>
    );
  }

  if (pageLoader.isError) {
    return (
      <PageShell>
        <BannerMessages {...pageLoader} />
      </PageShell>
    );
  }

  if (!isEligible) {
    return (
      <PageShell>
        <Banner variant="error">{ACH_UNAVAILABLE}</Banner>
      </PageShell>
    );
  }

  if (createLoader.isError) {
    return (
      <PageShell>
        <BannerMessages {...createLoader} />
        <Button className="w-fit" onClick={startSetupIntent}>
          Try again
        </Button>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Banner variant="info" className="mb-4">
        Most bank accounts can be verified instantly. If your bank requires
        microdeposits, you can finish verification from the payment methods
        page.
      </Banner>
      <Box>
        {clientSecret ? (
          <StripeProvider
            options={{ clientSecret, appearance: { theme: "stripe" } }}
          >
            <AchSetupForm
              billingDetailId={billingDetailId}
              email={user.email}
              confirmedSetupIntentId={confirmedSetupIntentId}
              onConfirmedSetupIntent={setConfirmedSetupIntentId}
            />
          </StripeProvider>
        ) : (
          <Loading />
        )}
      </Box>
    </PageShell>
  );
};
